import { createContext, PropsWithChildren, useContext, useState } from "react";
import { api } from "../api/API";
import { useCurrentUser } from "./CurrentUser";

export type UIFlag = "welcome" | "swipe" | "hint-project" | "hint-repeat" | "hint-team";

const UIFlagContext = createContext({
    has: (key: UIFlag) => false as boolean,
    set: (key: UIFlag, value: boolean) => {},
});

// UIFlagProvider holds which hints the user has seen. A change shows at once and is
// saved in the background, so the next ping agrees with it.
export function UIFlagProvider(props: PropsWithChildren<{}>) {
    const me = useCurrentUser();
    const [local, setLocal] = useState<Partial<Record<UIFlag, boolean>>>({});

    const has = (key: UIFlag) => {
        if (key in local) return !!local[key];
        return !!me?.uiFlags?.[key];
    };

    const set = (key: UIFlag, value: boolean) => {
        if (has(key) === value) return;

        setLocal(prev => ({ ...prev, [key]: value }));

        // a hint is not worth interrupting the user for: when the save fails, the
        // hint shows once more on the next visit
        api.users.setUIFlag({ key: key, value: value }).catch(e => console.error(e));
    };

    return (
        <UIFlagContext.Provider value={{ has: has, set: set }}>
            {props.children}
        </UIFlagContext.Provider>
    );
}

export function useUIFlags() {
    return useContext(UIFlagContext);
}
